// The signed library bundle (spec/10 §7.1, §5.2, §6.2; F-CAT-02, F-CAT-05).
//
// What a station may launch: the venue library's visible entries for this station's class, each with the
// catalogue game's name, cover, hero, logo, trailer and launch recipes. It rides the same signed transport as the policy bundle
// (policy-bundle.ts): a compact JWS (EdDSA) over the api JWKS whose payload is the `LibraryBundle` below
// plus `iss` and `aud` (`LIBRARY_BUNDLE_AUDIENCE`). The station verifies it, hands the entries to the
// agent's launch cache (the agent resolves `entryId → LaunchRecipe` when the kiosk asks to play) and the
// catalogue to the kiosk's Games screen. The kiosk launches by `entryId`.
//
// **Apps ride it exactly like games** (spec/14 §6): an entry's `kind` says which it is, an app's square icon
// is its `coverUrl` (and `iconUrl`), and every entry carries the title's `detection` rules — process names
// normalised without `.exe`, an optional path pattern, an optional regex (spec/10 §7.1, §7.3) — for the
// station's in-session detection. All three are optional so a bundle signed before them still parses.
// Install detection (F-CAT-03) is the station's own: it scans the PC for these entries' recipes and reports
// the result (`routes/station-installs.ts`); nothing about what is installed rides the signed bundle. An app that installs per
// Windows account carries its provisioning recipe and its sign-out paths (F-CAT-09, ADR-0045), each only when set. `requiresElevation`
// (spec/11 §3.5) rides it only when it is on — the catalogue's flag, unless the venue turned it off for itself — so a
// library with no elevated title signs exactly as before. Not carried yet: `antiCheat` and `ageRating` (spec/10 §7.1).
import { z } from "../zod.js";
import { ID_PREFIXES, LibraryEntryIdSchema, StationIdSchema } from "../ids/schema.js";
import { typeIdPattern } from "../ids/typeid.js";
import { AppProvisioningSchema, AppSignOutPathSchema, LaunchRecipeSchema, TitleDetectionSchema } from "../routes/hq-catalogue.js";
/** The `aud` a signed library bundle carries: never a token, a policy bundle or a lease. */
export const LIBRARY_BUNDLE_AUDIENCE = 'station-library';
/** The most trailers a library bundle entry carries for one game (`trailerUrls`, spec/14 §6): the newest five. */
export const LIBRARY_TRAILERS_PER_GAME = 5;
/** A launch recipe inside the bundle: the catalogue's `LaunchRecipe`, named apart for the station's realtime DTOs. */
// Rebuilt from its shape, not `.meta()` on the route schema (see lease.ts's LeasePlanSegment for why).
export const LibraryLaunchRecipeSchema = z.strictObject(LaunchRecipeSchema.shape).meta({ id: 'LibraryLaunchRecipe' });
/**
 * A title's detection rules inside the bundle (spec/10 §7.3): the catalogue's `TitleDetection`, named apart for
 * the station's realtime DTOs. `processNames` are lower case without `.exe`; `pathPattern` is a wildcard over
 * the process image path (path variables resolved on the PC) that narrows a name match; `regex` is a
 * case-insensitive expression over the image path that matches on its own.
 */
export const LibraryDetectionSchema = z.strictObject(TitleDetectionSchema.shape).meta({ id: 'LibraryDetection' });
/**
 * An app's provisioning recipe inside the bundle (F-CAT-09, ADR-0045): the catalogue's `AppProvisioning`, named apart for the
 * station's DTOs, plus `install` — whether the venue turned **Install for players** on. The recipe rides even when it is
 * off, so the station can tell the console an app is installed for another Windows account only.
 */
export const LibraryProvisioningSchema = z
    .strictObject({
    ...AppProvisioningSchema.shape,
    /** The venue turned Install for players on: keep the app installed in the player account. */
    install: z.boolean(),
})
    .meta({ id: 'LibraryProvisioning' });
/** Where an app keeps a player's sign-in, inside the bundle (F-CAT-09, spec/11 §5): the catalogue's `AppSignOutPath`. */
export const LibrarySignOutPathSchema = z.strictObject(AppSignOutPathSchema.shape).meta({ id: 'LibrarySignOutPath' });
/**
 * The title an entry offers: a catalogue title's `gam_…` id, or — for an organisation's custom app (spec/13 §9) —
 * its `app_…` id. A plain string to the station, which keys nothing on it (it launches by `entryId`).
 */
const BundleTitleIdSchema = z
    .string()
    .regex(new RegExp(`(?:${typeIdPattern(ID_PREFIXES.game).source})|(?:${typeIdPattern(ID_PREFIXES.customApp).source})`), 'A catalogue title id (gam_…) or a custom app id (app_…).');
/** One title or app the station may launch (spec/10 §7.1). */
export const LibraryBundleEntrySchema = z
    .object({
    /** The venue library entry — the id the kiosk launches by. */
    entryId: LibraryEntryIdSchema,
    /** The catalogue title (`gam_…`), or the organisation's custom app (`app_…`) this entry offers. */
    gameId: BundleTitleIdSchema,
    name: z.string().min(1),
    /**
     * `game` or `app` (spec/14 §6). Optional so a bundle signed before apps existed still parses; absent means
     * `game`. The kiosk files entries by `category`; this is for the station's own bookkeeping.
     */
    kind: z.enum(['game', 'app']).optional(),
    /** The cover (264 × 374) — for an app, its square icon — or null for the initials placeholder. */
    coverUrl: z.string().nullable(),
    /** The square icon on our media CDN (an app's tile), or null. Cached by the station like the cover. */
    iconUrl: z.string().nullable().optional(),
    /**
     * The wide hero art on our media CDN, or null (ADR-0028). Optional so a bundle signed before it existed
     * still parses; the station downloads it into its media cache and the kiosk loads the local copy.
     */
    heroUrl: z.string().nullable().optional(),
    /** The game's transparent logo on our media CDN, or null (ADR-0028). Cached by the station like the hero. */
    logoUrl: z.string().nullable().optional(),
    /**
     * The game's most recent `ready` trailer on our media CDN, or null (ADR-0028). The station caches it and
     * the kiosk plays it natively — muted and looped on the lock screen for the featured game.
     */
    trailerUrl: z.string().nullable().optional(),
    /**
     * Every `ready` trailer of the game on our media CDN, newest first, at most
     * `LIBRARY_TRAILERS_PER_GAME` (ADR-0028, spec/12 §3.1). The station caches each one; the kiosk's lock screen
     * plays them in turn as its trailer playlist. `trailerUrl` is always the first (or null when this is empty),
     * kept for stations and kiosks that predate the list. Optional so a bundle signed before it still parses;
     * absent means `[trailerUrl]` when that is set.
     */
    trailerUrls: z.array(z.string()).max(LIBRARY_TRAILERS_PER_GAME).optional(),
    /** The venue's category for the Games rail, or null. */
    category: z.string().nullable(),
    featured: z.boolean(),
    sortOrder: z.int(),
    /** The ways to launch it, in the catalogue's order; the station uses the first that resolves. */
    recipes: z.array(LibraryLaunchRecipeSchema),
    /**
     * How the station recognises the title's process (spec/10 §7.3) — install discovery and in-session
     * detection. Optional so a bundle signed before it existed still parses; absent means no rules.
     */
    detection: LibraryDetectionSchema.optional(),
    /**
     * The title needs administrator rights (spec/11 §3.5): the station routes its launch to the elevated service and
     * never runs it elevated itself. Present (and `true`) only when the catalogue flags it and the venue has not
     * turned it off; absent means a normal launch.
     */
    requiresElevation: z.boolean().optional(),
    /**
     * How the station installs the app into the player account, and whether the venue asked it to (F-CAT-09, ADR-0045).
     * Present only for a catalogue app with a provisioning recipe, so a library without one signs exactly as before; an
     * older station ignores it.
     */
    provisioning: LibraryProvisioningSchema.optional(),
    /**
     * Where the app keeps a player's sign-in (F-CAT-09): cleared at every session end with the station's own list of
     * launcher sign-ins (spec/11 §5). Present only when the catalogue app names some.
     */
    signOutPaths: z.array(LibrarySignOutPathSchema).optional(),
})
    .meta({ id: 'LibraryBundleEntry' });
/** The bundle's content — the JWS payload, less `iss`/`aud`/`iat`. */
export const LibraryBundleSchema = z
    .object({
    /** A content hash: the station replaces its library only when this differs from the one it holds. */
    version: z.string().min(1).max(64),
    stationId: StationIdSchema,
    entries: z.array(LibraryBundleEntrySchema),
})
    .openapi('LibraryBundle', { description: 'What a station may launch, and how (spec/10 §7.1).' });
//# sourceMappingURL=library.js.map